import {
    Component,
    h,
    Prop,
    ComponentInterface,
    Host,
    Element,
    Event,
    Watch,
    Method,
    EventEmitter,
} from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";
import { hasSlot } from "../../utils/utils";
import PopoverImpl from "../../utils/popover";

export type WizardStepState = "default" | "in-progress" | "error" | "done";

/**
 * @slot - Set the label/name of this step by using the default slot
 * @slot flag - Set a custom flag next to the step number
 * @slot index - Set a custom content for the index rectangle. E.g. use an icon instead of the index number.
 * @slot flyout-content - Set the flyout content. Suggested content: a headline, copytext, lists, links for further information
 * @slot flyout-footer - Set a custom flyout footer button(s), can be used to enabled custom actions or to prevent the default flyout closing action
 */
@Component({
    tag: "uic-wizard-header-step",
    styleUrl: "wizard-header-step.scss",
})
export class WizardHeaderStep implements ComponentInterface {
    private flyout: HTMLUicFlyoutElement;
    private popover: PopoverImpl;

    @Element() host: HTMLUicWizardHeaderStepElement;

    /**
     * The name of the flag to show next to the step number. May be overwritten by the "flag" slot.
     */
    @Prop() flag?: string;

    /**
     * The text shown next to the step number. May be overwritten by the default slot.
     */
    @Prop() label?: string;

    /**
     * If set to `true`, the wizard header orientation is considered to be vertical.
     */
    @Prop({ reflect: true }) vertical = false;

    /**
     * If set to `true`, the user cannot interact with the button.
     */
    @Prop({ reflect: true }) disabled = false;

    /**
     * If set to `true`, the button is considered selected or toggled.
     */
    @Prop({ reflect: true }) selected = false;

    /**
     * Select the state of this step. Used to show the default flags.
     */
    @Prop({ reflect: true }) state: WizardStepState = "default";

    /**
     * The index of this step, displayed as number in the component. 1-based.
     */
    @Prop({ reflect: true }) index: number = 1;

    /**
     * The value used to uniquely identify the step.
     */
    @Prop({ reflect: true, mutable: true }) name: string;

    /**
     * If set to `true`, the flyout is open(ed). Setting it to true will only take effect when the step is selected.
     */
    @Prop({ reflect: true, mutable: true }) flyoutOpen: boolean = false;

    /**
     * If set to `true`, the flyout is automatically opened when the step is selected.
     */
    @Prop({ reflect: true }) autoFlyout: boolean = false;

    /**
     * Set as custom label for the button of the flyout. Defaults to 'Close' which is not translated.
     * To use custom buttons the slot "flyout-footer" may be used.
     */
    @Prop({ reflect: true }) flyoutButtonLabel: string = "Close";

    /**
     * Indicates this step requesting to be selected.
     */
    @Event({ eventName: "uicWizardStepSelect" })
    selectRequest: EventEmitter<void>;

    @Watch("selected")
    onSelectedChange() {
        if (this.selected) {
            this.select();
        }

        this.updateFlyoutState();
    }

    @Watch("flyoutOpen")
    onFlyoutOpenChange() {
        if (!this.flyout) {
            return;
        }

        // do not open the flyout if this step is not selected
        if (!this.selected) {
            this.flyoutOpen = false;
        }

        if (this.flyoutOpen) {
            this.popover.setOptions({ placement: this.getFlyoutPlacement() });
        }
    }

    componentWillLoad() {
        if (!this.name) {
            this.name = getUniqueId();
        }

        // set inital flyout state
        this.updateFlyoutState();
    }

    componentDidLoad() {
        if (this.flyout) {
            const flyoutPlacement = this.getFlyoutPlacement();

            this.popover = new PopoverImpl(this.host, this.flyout, {
                placement: flyoutPlacement,
                // move the flyout to the right to prevent cut off
                preventOverflow: true,
            });

            // trigger initial popper placement for opened flyouts
            this.onFlyoutOpenChange();
        }
    }

    componentDidUpdate() {
        if (!this.popover || !this.flyout) {
            return;
        }

        requestAnimationFrame(() => this.popover.update());
    }

    disconnectedCallback() {
        this.flyoutOpen = false;
        if (this.popover) {
            this.popover.destroy();
        }
    }

    /**
     * Requests current step to become selected one in its containing wizard.
     */
    @Method()
    async select() {
        if (!this.disabled) {
            this.selectRequest.emit();
        }
    }

    /**
     * Toggle the flyout, only has an effect while the step is selected
     */
    @Method()
    async toggleFlyout() {
        this.flyoutOpen = !this.flyoutOpen;
    }

    private updateFlyoutState() {
        // set or keep flyout state
        if (this.selected) {
            this.flyoutOpen = this.autoFlyout || this.flyoutOpen;
        } else {
            this.flyoutOpen = false;
        }
    }

    private getFlyoutPlacement() {
        const flyoutWidth = parseInt(
            getComputedStyle(this.flyout).getPropertyValue(
                "--uic-flyout-width"
            ) || "320px" // fallback to the default when CSS vars are not set/supported
        );
        const flyoutPlacement = this.vertical
            ? "right-end"
            : this.host.offsetWidth > flyoutWidth
            ? "bottom"
            : "bottom-end";

        return flyoutPlacement;
    }

    private get tabIndex() {
        if (this.disabled) {
            return -1;
        }
        const hasTabIndex = this.host.hasAttribute("tabindex");

        if (hasTabIndex) {
            return this.host.getAttribute("tabindex") as any;
        }

        return 0;
    }

    private getFlag(): string {
        if (this.flag) {
            return this.flag;
        }

        if (this.state === "done") {
            return "uic:flag-checked";
        }

        if (this.state === "error") {
            return "uic:flag-error";
        }

        if (this.state === "in-progress") {
            return "uic:flag-in-progress";
        }

        return "";
    }

    private handleClick(e: Event) {
        if (e.defaultPrevented) {
            return;
        }

        if (this.selected && this.flyout) {
            this.flyoutOpen = !this.flyoutOpen;
            return;
        }
        if (!this.selected) {
            this.select();
        }
    }

    private renderFlyoutIndicator() {
        return (
            <uic-icon
                class="uic-wizard-header-step__flyout-indicator"
                name={this.flyoutOpen ? "uic:chevron-down" : "uic:chevron-up"}
            ></uic-icon>
        );
    }

    private renderFlyout() {
        return (
            <uic-flyout
                class="uic-wizard-header-step__flyout"
                position="right"
                opened={this.flyoutOpen}
                ref={(el) => (this.flyout = el)}
            >
                <div slot="content">
                    <slot name="flyout-content"></slot>
                </div>
                <div slot="footer">
                    <slot name="flyout-footer">
                        <uic-button variant="footer-primary">
                            {this.flyoutButtonLabel}
                        </uic-button>
                    </slot>
                </div>
            </uic-flyout>
        );
    }

    render() {
        const flag = this.getFlag();
        const hasCustomFlag = hasSlot(this.host, "flag");
        const hasLabelAsSlot = hasSlot(this.host);
        const hasFlyout = hasSlot(this.host, "flyout-content");

        return (
            <Host
                role="tab"
                aria-disabled={this.disabled}
                aria-selected={this.selected}
                tabindex={this.tabIndex}
                onclick={(e) => this.handleClick(e)}
            >
                <div class="uic-wizard-header-step__number">
                    <span
                        class={{
                            "uic-wizard-header-step__flag": true,
                            "uic-wizard-header-step__flag--custom": hasCustomFlag,
                        }}
                    >
                        <slot name="flag">
                            {flag && <uic-icon name={flag}></uic-icon>}
                        </slot>
                    </span>
                    <slot name="index">
                        <span>{this.index}</span>
                    </slot>
                </div>
                <div
                    class={{
                        "uic-wizard-header-step__label-wrapper": true,
                        "uic-wizard-header-step__label-wrapper--with-flyout": hasFlyout,
                    }}
                >
                    <span class="uic-wizard-header-step__label">
                        <slot>{!hasLabelAsSlot && this.label}</slot>
                    </span>
                    {hasFlyout && this.renderFlyoutIndicator()}
                </div>
                {hasFlyout && this.renderFlyout()}
            </Host>
        );
    }
}
